<script setup>
import { useMutation, useQueryClient, useQuery } from '@tanstack/vue-query'
import { computed, ref, watch, reactive } from 'vue'

import LokiSpeechAnimation from '@/assets/lotties/loki-speech-animation-v2-optimized.json'
import SimpleConfirmationModal from '@/components/modals/confirmation/SimpleConfirmationModal.vue'
import CollapsableFloatingPanel from '@/components/sword-desk/CollapsableFloatingPanel.vue'
import ExistingTicket from '@/components/sword-desk/ExistingTicket.vue'
import router from '@/router'
import i18n from '@/scripts/app-configs/i18n-config'
import useLottie from '@/composables/useLottie'
import { useSwordDeskTicketsFetch } from '@/composables/useSwordDeskTicketsFetch'
import useSwordForm from '@/composables/useSwordForm'
import { $http, $notify } from '@/plugins'
import { events, triggers } from '@/scripts/global-modals-commands'
import { TicketStateEnum } from '@/types/sword-desk/tickets/ticket.types'
import { swordDeskSchema as validationSchema, SWORD_DESK_MAX_FILE_SIZE_MB } from '@/utils/zod/schemas/sword-desk'
import { segment } from '@/utils/segment'
import { EVENT_KEYS } from '@/utils/segment/segment-events'

const props = defineProps({
  modalOpen: {
    type: Boolean,
    default: false,
  },
  payload: {
    type: Object,
    default: () => ({}),
  },
})

const queryClient = useQueryClient()

const ticketPanelRef = ref()
const formState = reactive({
  ticketTitle: '',
  ticketDescription: '',
  ticketUuid: '',
  fileForUpload: new File([], ''),
})
const showConfirmTicketDeletionModal = ref(false)
const creatingNewTicket = ref(false)
const initialTicketsLoadAttempted = ref(false)
const confirmationModalPurpose = ref('')
const isClinicalEscalation = ref(false)

const isModalOpen = computed(() => props.modalOpen)
const memberAccountUuid = computed(() => props.payload.memberAccountUuid)
const memberProgramUuid = computed(() => props.payload.memberProgramUuid)
const memberStatus = computed(() => props.payload.memberStatus)
const masterTaskId = computed(() => props.payload.masterTaskId)
const createTicketSource = computed(() => props.payload.createTicketSource)

const panelTitle = computed(() => creatingNewTicket.value
  ? i18n.t('SWORD_DESK.CREATE_TICKET')
  : i18n.t('SWORD_DESK.SUPPORT_TICKETS'),
)
const lottieContainer = ref()

useLottie(lottieContainer, LokiSpeechAnimation, { segments: ['9', '10'] })

const { $v } = useSwordForm({
  validationSchema,
  formState,
})

const resetNewTicketForm = () => {
  formState.ticketTitle = ''
  formState.ticketDescription = ''
  formState.ticketUuid = ''
  formState.fileForUpload = new File([], '')
  isClinicalEscalation.value = false
  if ($v.value?.$reset) {
    $v.value.$reset()
  }
}

// Fetch existing tickets
const {
  tickets: openTickets,
  isLoading: loadingExistingTickets,
} = useSwordDeskTicketsFetch({
  memberAccountUuid,
  enableQuery: computed(() => !!memberAccountUuid.value && isModalOpen.value),
})

const filteredOpenTickets = computed(() => {
  return (openTickets.value?.filter((ticket) => ticket.state !== TicketStateEnum.CLOSED) || [])
    .sort((a, b) => {
      // First sort by state (SOLVED goes to end)
      if (a.state === TicketStateEnum.SOLVED && b.state !== TicketStateEnum.SOLVED) return 1
      if (a.state !== TicketStateEnum.SOLVED && b.state === TicketStateEnum.SOLVED) return -1

      // Then sort by updated_at
      return new Date(b.updated_at) - new Date(a.updated_at)
    })
})

// Fetch AI-generated prefill data for a new ticket
const { data: prefillData, isFetching: aiGeneratingTicket } = useQuery({
  queryKey: ['sword-desk-ticket-prefill', memberAccountUuid],
  enabled: computed(() =>
    !!memberAccountUuid.value &&
        isModalOpen.value &&
        creatingNewTicket.value === true &&
        !formState.ticketUuid,
  ),
  queryFn: async () => {
    const { data } = await $http('swordDesk/prefillTicket', memberProgramUuid.value)

    return data
  },
})

// Mutation to pre-upload a file and get a token
const { mutateAsync: updateFileAndGetToken } = useMutation({
  retry: 3,
  mutationFn: async (file) => {
    const formData = new FormData()

    formData.append('attachment', file)

    const { data } = await $http('swordDesk/uploadTicketFile', null, { body: formData })

    return data
  },
})

// Mutation to submit the new ticket
const { mutateAsync: submitTicket, isLoading: ticketBeingCreated } = useMutation({
  mutationFn: async ({ body, file }) => {
    if (file && file.name && file.size) {
      const { file_token } = await updateFileAndGetToken(file)

      body.fileTokens.push(file_token)
    }

    return $http('swordDesk/createTicket', null, { body })
  },
  onSuccess: async () => {
    $notify.success(i18n.t('SWORD_DESK.TICKET_SUBMITTED'), { timeout: 1500 })
    await queryClient.invalidateQueries(['sword-desk-tickets', memberAccountUuid])
    creatingNewTicket.value = false

    // Reset form fields
    resetNewTicketForm()
    if ($v.value?.$reset) $v.value.$reset()
  },
  onError: () => {
    $notify.error(i18n.t('copy_89'))
  },
})

/**
* Clears form fields and switches to the new ticket creation view.
* Resets `ticketUuid` to allow for a new AI prefill if applicable.
*/
const openNewTicketForm = () => {
  resetNewTicketForm()
  creatingNewTicket.value = true
}

const handleCancelNewTicket = () => {
  confirmationModalPurpose.value = 'discardCancelForm'
  showConfirmTicketDeletionModal.value = true
}

const handleClose = () => {
  if (creatingNewTicket.value) {
    confirmationModalPurpose.value = 'discardClosePanel'
    showConfirmTicketDeletionModal.value = true
  } else {
    triggers.closeSwordDesk()
  }
}

const handleConfirmationOk = () => {
  const purpose = confirmationModalPurpose.value

  showConfirmTicketDeletionModal.value = false
  confirmationModalPurpose.value = ''

  if (purpose === 'discardClosePanel') {
    triggers.closeSwordDesk()
  } else if (purpose === 'discardCancelForm') {
    creatingNewTicket.value = false
    resetNewTicketForm()

    if (filteredOpenTickets.value?.length === 0) {
      triggers.closeSwordDesk()
    }
  }
}

const handleConfirmationClose = () => {
  showConfirmTicketDeletionModal.value = false
  confirmationModalPurpose.value = ''
}

const submit = async () => {
  if ($v.value?.$touch) $v.value.$touch()
  if ($v.value?.$invalid) return

  const ticketRequestBody = {
    external_uuid: formState.ticketUuid || undefined,
    subject: formState.ticketTitle,
    description: formState.ticketDescription,
    fileTokens: [],
    reporter: {
      account_uuid: memberAccountUuid.value,
    },
    properties: [
      { name: 'program', value: memberProgramUuid.value },
      { name: 'tags', value: 'created_from_aifeed' },
      { name: 'clinical_escalation', value: isClinicalEscalation.value },
    ],
  }

  await submitTicket({ body: ticketRequestBody, file: formState.fileForUpload })

  trackTicketCreated()
}

const trackTicketCreated = () => {
  const eventProperties = {
    programUuid: memberProgramUuid.value,
    masterTaskId: masterTaskId.value,
    memberStatus: memberStatus.value,
    source: createTicketSource.value,
  }

  segment.trackEventWithUser(EVENT_KEYS.SD_TICKET_CREATED, eventProperties)
}

const toggleClinicalEscalation = () => {
  isClinicalEscalation.value = !isClinicalEscalation.value
}

/**
* Manages the view state (list vs. new ticket form) after existing tickets finish loading.
* If no tickets exist and not already in creation mode, switches to new ticket form.
*/
watch(loadingExistingTickets, (isLoading, wasLoading) => {
  if (isModalOpen.value && wasLoading && !isLoading) {
    initialTicketsLoadAttempted.value = true
    if (!creatingNewTicket.value && filteredOpenTickets.value?.length === 0) {
      creatingNewTicket.value = true
    }
  }
})

/**
* Handles state initialization when the modal opens and cleanup when it closes.
* Determines initial view (list or new ticket) based on cached/loaded tickets.
*/
watch(isModalOpen, (isOpen) => {
  if (isOpen) {
    initialTicketsLoadAttempted.value = false
    if (!loadingExistingTickets.value) {
      initialTicketsLoadAttempted.value = true
      creatingNewTicket.value = (filteredOpenTickets.value?.length === 0)
    } else {
      creatingNewTicket.value = false
    }
  } else {
    creatingNewTicket.value = false
    resetNewTicketForm()
    queryClient.removeQueries({ queryKey: ['sword-desk-ticket-prefill', memberAccountUuid.value] })
  }
}, { immediate: true })

/**
* Populates form fields when AI prefill data becomes available.
*/
watch(prefillData, (response) => {
  if (!response || !creatingNewTicket.value) return

  formState.ticketTitle = response.title || ''
  formState.ticketDescription = response.description || ''
  formState.ticketUuid = response.ticket_uuid || ''

  // Optionally touch fields for Vuelidate if prefilled, to clear initial "required" messages
  if (response.title && $v.value?.ticketTitle?.$touch)
    $v.value.ticketTitle.$touch()
  if (response.description && $v.value?.ticketDescription?.$touch)
    $v.value.ticketDescription.$touch()
})

// Detecting automatic ticket closing conditions and finner UX rules based on global modals activities
events.onGlobalModalTrigger((modalKey, action, payload) => {
  if (!isModalOpen.value) return
  // Close if another member's sidebar is opened
  if (modalKey === 'memberSidebar' && payload && payload?.memberUuid !== memberProgramUuid.value) {
    triggers.closeSwordDesk()
  }

  // If SwordDesk is triggered again for the same member, uncollapse panel if it was collapsed
  if (modalKey === 'swordDesk' && payload?.memberProgramUuid === memberProgramUuid.value) {
    if (ticketPanelRef.value?.panelCollapsed?.value) {
      ticketPanelRef.value.togglePanelCollapsing()
    }
  }
}, { strictTopic: ['memberSidebar', 'swordDesk'] })

router.beforeEach(async (to, from, next) => {
  if (isModalOpen.value) triggers.closeSwordDesk()
  next()
})

/**
* Determines the current global loading state of the panel for UI feedback.
* Returns a string key for different loading messages or null if not loading.
*/
const computedIsLoadingPanel = computed(() => {
  if (!initialTicketsLoadAttempted.value && loadingExistingTickets.value) {
    return 'LOADING_EXISTING_TICKETS'
  }

  if (creatingNewTicket.value) {
    if (ticketBeingCreated.value) return 'SUBMITTING_NEW_TICKET'
    if (aiGeneratingTicket.value) return 'AI_PREFILLING_TICKET'
  }

  return null
})

</script>

<template>
  <div>
    <collapsable-floating-panel
      v-if="modalOpen"
      ref="ticketPanelRef"
      :title="$t(panelTitle)"
      class="global-modal-container"
      ignore-click-outside-ui-actions="open-sword-desk"
      :collapse-on-click-outside="!showConfirmTicketDeletionModal"
      @close="handleClose"
    >
      <div v-if="computedIsLoadingPanel" class="ai-loading flex h-full flex-col items-center justify-center px-16">
        <div
          key="loading-lottie-container"
          ref="lottieContainer"
          class="ai-loading__animation mb-2"
        ></div>
        <p class="ai-loading__text pb-16 text-center text-sm font-semibold">
          <template v-if="computedIsLoadingPanel === 'LOADING_EXISTING_TICKETS'">
            {{ $t('SWORD_DESK.LOADING_TICKETS') }}
          </template>
          <template v-else-if="computedIsLoadingPanel === 'SUBMITTING_NEW_TICKET'">
            {{ $t('SWORD_DESK.SUBMITTING_TICKET') }}
          </template>
          <template v-else-if="computedIsLoadingPanel === 'AI_PREFILLING_TICKET'">
            {{ $t('SWORD_DESK.CREATING_TICKET') }}
          </template>
        </p>
      </div>

      <div v-else-if="!creatingNewTicket & filteredOpenTickets.length > 0">
        <ul>
          <existing-ticket
            v-for="(ticket, key) in filteredOpenTickets"
            :key="key"
            class="-mx-4 px-4"
            :title="ticket.title"
            :updated-at="ticket.updated_at"
            :state="ticket.state"
            :link="ticket.link"
          />
        </ul>
      </div>

      <div v-else class="my-5 flex h-[calc(100%-40px)] grow flex-col gap-4">
        <div class="flex items-center gap-4">
          <sword-text-wuk
            v-model="formState.ticketTitle"
            label="Title"
            label-as-placeholder
            fill-bg
            :validation="$v.ticketTitle"
          />
          <sword-button-wuk
            v-tooltip="$t('SWORD_DESK.TOOLTIP.CLINICAL_ESCALATION')"
            icon="arrow-up-escalate"
            :class="[
              '!mb-6 shrink-0 !rounded-lg hover:!text-negative-600',
              { '!bg-neutral-200': !isClinicalEscalation },
              { '!bg-negative-100 !text-negative-600': isClinicalEscalation }
            ]"
            theme="transparent"
            round
            @click.native="toggleClinicalEscalation"
          />
        </div>
        <sword-textarea-wuk
          v-model="formState.ticketDescription"
          class="grow"
          :label="$t('copy_3462')"
          :placeholder="$t('copy_3462')"
          :validation="$v.ticketDescription"
          label-as-placeholder
          fill-bg
        />
        <sword-file-upload-wuk
          v-model="formState.fileForUpload"
          placeholder="File upload"
          :validation="$v.fileForUpload"
          :help-text="$t('INPUTS.HELP_TEXTS.FILE_UPLOAD', { maxFiles: 1, maxFileSize: SWORD_DESK_MAX_FILE_SIZE_MB })"
          fill-bg
        />
      </div>

      <template #footer>
        <div v-if="!computedIsLoadingPanel">
          <div v-if="creatingNewTicket" class="flex gap-4">
            <sword-button-wuk
              class="inline-flex grow"
              text="COMMON.CANCEL"
              theme="secondary"
              size="medium"
              @click.native="handleCancelNewTicket"
            />
            <sword-button-wuk
              class="inline-flex grow"
              text="COMMON.SUBMIT"
              theme="primary"
              size="medium"
              @click.native="submit"
            />
          </div>
          <sword-button-wuk
            v-else
            class="inline-flex w-full grow"
            icon="add"
            side="left"
            text="Create new ticket"
            theme="primary"
            size="medium"
            @click.native="openNewTicketForm"
          />
        </div>
      </template>
    </collapsable-floating-panel>

    <!-- Confirm ticket deletion -->
    <simple-confirmation-modal
      v-if="showConfirmTicketDeletionModal"
      header="SWORD_DESK.DELETE_TICKET"
      body=""
      @close="handleConfirmationClose"
      @cancel="handleConfirmationClose"
      @ok="handleConfirmationOk"
    />
  </div>
</template>

<style scoped lang="scss">
.ai-loading {
  overflow: hidden;
  &__animation {
    width: 6rem;
    height: 6rem;
    margin: 0 auto;
  }
}

.sword-modal-mask {
  ::v-deep .sword-modal-body {
    margin-top: 1rem !important;
    margin-bottom: 0 !important;
  }
}

:deep(.sword-boxed-input-wuk) {
  height: 100%;
  max-height: calc(100% - 24px);

  & > textarea {
    height: 100%;
  }
}
</style>
